import { useState, useEffect } from "react";
import Header from "../header";
import Footer from "../footer";
import DatePicker from "../../components/DatePicker";
import { useNavigate, useLocation } from "react-router-dom";
import FileUpload from "../../components/FileUpload";
import { loadRelationships, saveDraft, fetchIdProofs } from "apiCalls/applyClaimService";
import { KSMART_BASE_URL } from "../../constants";
import { useLanguage } from "context/LanguageContext";
function maskAadhaar(val) {
    if (!val || val === "-") return "-";
    const digits = String(val).replace(/\D/g, "");
    if (digits.length === 12) return `XXXX XXXX ${digits.slice(8)}`;
    return "XXXX XXXX XXXX";
}

function DeathClaim() {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { t } = useLanguage();
    const loan = state?.loan || {};

    const initialClaim = state?.freshClaim ? {} : (state?.claim || {});

    const [claimantName, setClaimantName] = useState(initialClaim.claimantName || "");
    const [relationship, setRelationship] = useState(initialClaim.relationship || "");
    const [relationshipName, setRelationshipName] = useState(initialClaim.relationshipName || "");
    const [relationships, setRelationships] = useState([]);
    const [loadingRelationships, setLoadingRelationships] = useState(false);
    const [relationshipsError, setRelationshipsError] = useState("");
    const [otherRelationship, setOtherRelationship] = useState(initialClaim.otherRelationship || "");
    const [dateOfDeath, setDateOfDeath] = useState(initialClaim.dateOfDeath || "");
    const [deathCertNumber, setDeathCertNumber] = useState(initialClaim.deathCertNumber || "");
    const [previousDiseaseRelief, setPreviousDiseaseRelief] = useState(initialClaim.previousDiseaseRelief || "yes");
    const [releaseAmount, setReleaseAmount] = useState(initialClaim.releaseAmount || "");
    const [dodCertificateFile,setDodCertificateFile] = useState(state?.files?.dodCertificateFile || null)
    const [dobProofDocType, setDobProofDocType] = useState(initialClaim.dobProofDocType || "")
    const [dobProofDocFile, setDobProofDocFile] = useState(state?.files?.dobProofDocFile || null)
    const [anotherDocName, setAnotherDocName] = useState(initialClaim.anotherDocName || "")
    const [anotherDocFile, setAnotherDocFile] = useState(state?.files?.anotherDocFile || null)
    const [idProofTypes, setIdProofTypes] = useState([])
    const [loadingIdProofTypes, setLoadingIdProofTypes] = useState(false)
    const [errors,setErrors] = useState({})
    const [declared,setDeclared] = useState(false)
    const [declarationError, setDeclarationError] = useState("")
    const [draftSaved, setDraftSaved] = useState(false)
    const [draftSaving, setDraftSaving] = useState(false)
    const [draftError, setDraftError] = useState("")
    const [draftId, setDraftId] = useState(state?.draftId || null)

    const handleSaveDraft = async () => {
        setDraftSaving(true);
        setDraftError("");
        try {
            const fd = new FormData();
            fd.append("risk_fund_claim_type_id", btoa("2"));
            fd.append("loan_number", loan.loanNo);
            if (draftId) fd.append("risk_fund_claim_id", draftId);
            if (claimantName) fd.append("claimant_name", claimantName);
            if (relationship) fd.append("relationship_to_loanee_id", relationship);
            fd.append("other_relation", otherRelationship || "");
            if (dateOfDeath) fd.append("date_of_death", dateOfDeath);
            if (deathCertNumber) fd.append("death_certificate_number", deathCertNumber);
            fd.append("is_previous_relief_taken", previousDiseaseRelief === "yes" ? 1 : 0);
            if (previousDiseaseRelief === "yes" && releaseAmount) fd.append("previous_relief_amount", releaseAmount);
            if (dodCertificateFile) fd.append("dod_proof_file", dodCertificateFile);
            if (dobProofDocType) {
                const selectedIdProof = idProofTypes.find(c => String(c.id) === String(dobProofDocType));
                const idProofName = selectedIdProof ? (t.idProofNames?.[selectedIdProof.name] || selectedIdProof.name) : "";
                if (idProofName) fd.append("id_proof_name", idProofName);
            }
            if (dobProofDocFile) fd.append("id_proof_file", dobProofDocFile);
            if (anotherDocName) fd.append("other_document_name", anotherDocName);
            if (anotherDocFile) fd.append("other_document_file", anotherDocFile);

            const res = await saveDraft(fd);

            const id = res?.data?.risk_fund_claim_id;
            if (id) {
                setDraftId(id);
            }
            setDraftSaved(true);
        } catch (err) {
            setDraftError(err.message || t.saveDraftError);
        } finally {
            setDraftSaving(false);
        }
    }

    useEffect(() => {
        setLoadingIdProofTypes(true);
        fetchIdProofs()
            .then(res => {
                const list = Array.isArray(res?.data) ? res.data : [];
                setIdProofTypes(list);
            })
            .catch(() => setIdProofTypes([]))
            .finally(() => setLoadingIdProofTypes(false));
    }, []);

    useEffect(() => {
        setLoadingRelationships(true);
        loadRelationships()
            .then(res => setRelationships(Array.isArray(res?.data) ? res.data : []))
            .catch(() => {
                setRelationships([]);
                setRelationshipsError(t.loadRelationshipsError);
            })
            .finally(() => setLoadingRelationships(false));
    }, []);

    const isOtherSelected = relationshipName === "Other";

    useEffect(() => { setDeclared(false); }, [claimantName, relationship, otherRelationship, dateOfDeath, deathCertNumber, previousDiseaseRelief, releaseAmount, dodCertificateFile, isOtherSelected]);

    const allFieldsFilled = claimantName.trim() && relationship &&
        (!isOtherSelected || otherRelationship.trim()) &&
        dateOfDeath && deathCertNumber.trim() &&
        (previousDiseaseRelief === "no" || releaseAmount.trim()) && dodCertificateFile

    const handleCheckboxClick = () => {
        const newErrors = {
            claimantName: !claimantName.trim() ? t.claimantNameRequired : "",
            relationship: !relationship ? t.relationshipRequired : "",
            otherRelationship: isOtherSelected && !otherRelationship.trim() ? t.specifyRelationshipRequired : "",
            dateOfDeath: !dateOfDeath ? t.dateOfDeathRequired : "",
            deathCertNumber: !deathCertNumber.trim() ? t.deathCertRequired : "",
            releaseAmount: previousDiseaseRelief === "yes" && !releaseAmount.trim() ? t.releaseAmountRequired : "",
            dodCertificateFile: !dodCertificateFile ? t.uploadDodProofRequired : "",
        }
        const hasErrors = Object.values(newErrors).some(e => e)
        if (hasErrors) {
            setErrors(newErrors)
            return
        }
        setDeclared(prev => !prev)
    }

    const formatForKsmart = (dob) => {
        if (!dob) return "";
        if (dob.includes("/")) {
            const [dd, mm, yyyy] = dob.split("/");
            return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
        }
        return dob;
    };

    const getDobVerifyUrl = () => `${KSMART_BASE_URL}/ui/cr/public/verify-certificate/${loan.birthCertificateNumber}/${formatForKsmart(loan.dob)}`;

    const handleDodVerify = () => {
        const url = `${KSMART_BASE_URL}/ui/cr/public/verify-certificate/${deathCertNumber}/${formatForKsmart(dateOfDeath)}`;
        window.open(url, "_blank");
    };

    const handlePreview = () => {
        if (!declared) {
            setDeclarationError(t.pleaseCheckDeclaration || "Please check the declaration to proceed.");
            return;
        }
        setDeclarationError("");
        const selectedIdProof = idProofTypes.find(c => String(c.id) === String(dobProofDocType));
        const dobProofDocTypeName = selectedIdProof ? (t.idProofNames?.[selectedIdProof.name] || selectedIdProof.name) : "";
        if (draftId) sessionStorage.setItem("previewDraftId", draftId);
        const claimState = { claimantName, relationship, relationshipName, otherRelationship, dateOfDeath, deathCertNumber, previousDiseaseRelief, releaseAmount, dobProofDocType, dobProofDocTypeName, anotherDocName };
        const filesState = { dodCertificateFile, dobProofDocFile, anotherDocFile };
        navigate("/previewDeathClaim", { state: { loan, claim: claimState, files: filesState, draftId } });
    }

    return (
        <>
            <Header />

            {/* ==== Scroll up and progress start ==== */}
            <div id="scrollUp">
                <svg className="arrowup" viewBox="0 0 24 24" width="18" height="18">
                    <path d="M13 7.828V20h-2V7.828l-5.364 5.364-1.414-1.414L12 4l7.778 7.778-1.414 1.414L13 7.828z" fill="#fff" />
                </svg>
                <svg className="scrollprogress" width="40" height="40">
                    <circle className="progress-circle" cx="20" cy="20" r="18" strokeWidth="2" fill="none" stroke="#fff" strokeDasharray="113.1" strokeDashoffset="113.1" />
                </svg>
            </div>
            {/* ==== Scroll up and progress Ends ==== */}

            {/* ==== Page Banner Section Start ==== */}
            <section className="rs-page-banner rs-faq-page-banner pt-30 pb-30 mt-140">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-7">
                            <div className="rs-page-banner__content">
                                <h1 className="title">{t.deathClaimTitle}</h1>
                                <p>{t.deathClaimPageSubtitle}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {/* ==== Page Banner Section Ends ==== */}

            {/* ==== Page Section Start ==== */}
            <section className="rs-faq-page pt-50 pb-120">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-12">

                            {/* Loan Details */}
                            <h4 className="mb-3" style={{ fontWeight: 700 }}>{t.loanDetails}</h4>
                            <div className="list-type mb-5">
                                <div className="row">
                                <div className="col-md-3 mb-2">
                                <p>{t.loaneeFullName}</p>
                                <h4>{loan.name}</h4>
                            </div>
                            <div className="col-md-3 mb-2">
                                <p>{t.aadhaarNumber}</p>
                                <h4>{maskAadhaar(loan.aadhaar)}</h4>
                            </div>
                            <div className="col-md-3 mb-2">
                                <p>{t.mobileNumber}</p>
                                <h4>{loan.mobile}</h4>
                            </div>
                            <div className="col-md-3 mb-2">
                                <p>{t.loanNumber}</p>
                                <h4>{loan.loanNo}</h4>
                            </div>
                            <div className="col-md-3 mb-2">
                                <p>{t.loanAmount}</p>
                                <h4>{loan.amount}</h4>
                            </div>
                            <div className="col-md-3 mb-2">
                                <p>{t.societyMembership}</p>
                                <h4>{loan.membership}</h4>
                            </div>
                            <div className="col-md-3 mb-2">
                                <p>{t.societyName}</p>
                                <h4>{loan.society}</h4>
                            </div>
                             <div className="col-md-3 mb-2">
                                <p>{t.societyAddress}</p>
                                <h4>{loan.societyAddress}</h4>
                            </div>
                            <div className="col-md-3 mb-2">
                                <p>{t.dateOfBirth}</p>
                                <h4>{loan.dob}</h4>
                            </div>
                            <div className="col-md-3 mb-2">
                                <p>{t.birthCertificate}</p>
                                <h4>{loan.birthCertificateNumber}</h4>
                            </div>
                            <div className="col-md-3 mb-2">
                                <a href={getDobVerifyUrl()} target="_blank" rel="noopener noreferrer" style={{ color: "#1a5c4a", fontSize: "0.9rem" }}>
                                    <i className="fa fa-external-link me-1"></i> {t.viewDoc}
                                </a>
                            </div>
                            </div>
                            </div>
                            {/* Loan Details */}

                            {/* Death Claim Details */}
                            <h4 className="mb-3" style={{ fontWeight: 700 }}>{t.deathClaimDetails}</h4>
                            <div className="list-type mb-5">
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <div className="form-group">
                                            <label className="form-label">{t.claimantName} <span className="text-danger">*</span></label>
                                            <input type="text" className={`form-control ${errors.claimantName ? "is-invalid" : ""}`} value={claimantName} onChange={e => { setClaimantName(e.target.value); if (errors.claimantName) setErrors(prev => ({ ...prev, claimantName: "" })) }} placeholder={t.claimantNamePlaceholder} />
                                            {errors.claimantName && <div className="text-danger small mt-1">{errors.claimantName}</div>}
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <div className="form-group">
                                            <label className="form-label">{t.relationshipToLoanee} <span className="text-danger">*</span></label>
                                            <select
                                                className={`form-control ${errors.relationship ? "is-invalid" : ""}`}
                                                value={relationship}
                                                disabled={loadingRelationships}
                                                onChange={e => {
                                                    const selected = relationships.find(r => String(r.id) === String(e.target.value));
                                                    setRelationship(e.target.value);
                                                    setRelationshipName(selected?.name || "");
                                                    setOtherRelationship("");
                                                    if (errors.relationship) setErrors(prev => ({ ...prev, relationship: "", otherRelationship: "" }))
                                                }}
                                            >
                                                {loadingRelationships
                                                    ? <option value="">{t.loading}</option>
                                                    : <>
                                                        <option value="">{t.selectRelationship}</option>
                                                        {relationships.map(r => (
                                                            <option key={r.id} value={r.id}>{r.name}</option>
                                                        ))}
                                                      </>
                                                }
                                            </select>
                                            {errors.relationship && <div className="text-danger small mt-1">{errors.relationship}</div>}
                                            {relationshipsError && <div className="text-danger small mt-1">{relationshipsError}</div>}
                                        </div>
                                    </div>
                                    {isOtherSelected && (
                                        <div className="col-md-4 mb-3">
                                            <div className="form-group">
                                                <label className="form-label">{t.specifyRelationship} <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.otherRelationship ? "is-invalid" : ""}`}
                                                    placeholder={t.specifyRelationshipPlaceholder}
                                                    value={otherRelationship}
                                                    onChange={e => { setOtherRelationship(e.target.value); if (errors.otherRelationship) setErrors(prev => ({ ...prev, otherRelationship: "" })) }}
                                                />
                                                {errors.otherRelationship && <div className="text-danger small mt-1">{errors.otherRelationship}</div>}
                                            </div>
                                        </div>
                                    )}
                                    <div className="col-md-4 mb-3">
                                        <div className="form-group">
                                            <label className="form-label">{t.dateOfDeath} <span className="text-danger">*</span></label>
                                            <DatePicker value={dateOfDeath} onChange={val => { setDateOfDeath(val); if (errors.dateOfDeath) setErrors(prev => ({ ...prev, dateOfDeath: "" })) }} placeholder="DD/MM/YYYY" className={errors.dateOfDeath ? "is-invalid" : ""} />
                                            {errors.dateOfDeath && <div className="text-danger small mt-1">{errors.dateOfDeath}</div>}
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <div className="form-group">
                                            <label className="form-label">{t.deathCertificate} <span className="text-danger">*</span></label>
                                            <input type="text" className={`form-control ${errors.deathCertNumber ? "is-invalid" : ""}`} value={deathCertNumber} onChange={e => { setDeathCertNumber(e.target.value); if (errors.deathCertNumber) setErrors(prev => ({ ...prev, deathCertNumber: "" })) }} />
                                            {errors.deathCertNumber && <div className="text-danger small mt-1">{errors.deathCertNumber}</div>}
                                            {deathCertNumber && dateOfDeath && (
                                                <a href={`${KSMART_BASE_URL}/ui/cr/public/verify-certificate/${deathCertNumber}/${formatForKsmart(dateOfDeath)}`} target="_blank" rel="noopener noreferrer" style={{ color: "#1a5c4a", fontSize: "0.85rem" }}>
                                                    <i className="fa fa-external-link me-1"></i> {t.viewDoc}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <div className="form-group">
                                            <label className="form-label">{t.previousDiseaseReliefLabel} <span className="text-danger">*</span></label>
                                            <div className="pt-2">
                                                <label className="form-check form-check-inline">
                                                    <input name="radio-3" type="radio" className="form-check-input" value="yes" checked={previousDiseaseRelief === "yes"} onChange={e => setPreviousDiseaseRelief(e.target.value)} />
                                                    <span className="form-check-label">{t.yes}</span>
                                                </label>
                                                <label className="form-check form-check-inline">
                                                    <input name="radio-3" type="radio" className="form-check-input" value="no" checked={previousDiseaseRelief === "no"} onChange={e => { setPreviousDiseaseRelief(e.target.value); setReleaseAmount(""); setErrors(prev => ({ ...prev, releaseAmount: "" })) }} />
                                                    <span className="form-check-label">{t.no}</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <div className="form-group">
                                            <label className="form-label">{t.amountReceived} {previousDiseaseRelief === "yes" && <span className="text-danger">*</span>}</label>
                                            <input type="text" className={`form-control ${errors.releaseAmount ? "is-invalid" : ""}`} value={releaseAmount} onChange={e => { setReleaseAmount(e.target.value); if (errors.releaseAmount) setErrors(prev => ({ ...prev, releaseAmount: "" })) }} disabled={previousDiseaseRelief === "no"} />
                                            {errors.releaseAmount && <div className="text-danger small mt-1">{errors.releaseAmount}</div>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Death Claim Details */}


                            {/* Documents Upload */}
                            <h5 className="mb-2">{t.dodProofUpload}</h5>
                            <div className="list-type mb-4">
                                <div className="row">
                                    <div className="col-md-12">
                                        <FileUpload label={t.uploadDodProof} initialFile={dodCertificateFile} onChange={(f)=>{setDodCertificateFile(f); if (f) setErrors(prev => ({ ...prev, dodCertificateFile: "" }))}}/>
                                        {errors.dodCertificateFile && <div className="text-danger small mt-1">{errors.dodCertificateFile}</div>}
                                    </div>
                                </div>
                            </div>
                            {/* Documents Upload */}

                            {/* Additional Documents */}
                            <h5 className="mb-2">{t.additionalDocuments}</h5>
                            <div className="list-type mb-5">
                                <div className="row">
                                    {/* Left: dropdown + ID proof upload | Right: text input + another doc upload */}
                                    <div className="col-md-6">
                                        <label className="form-label">{t.chooseValidDocument}</label>
                                        <div style={{ position: "relative" }}>
                                            <select
                                                className="form-control"
                                                value={dobProofDocType}
                                                onChange={e => setDobProofDocType(e.target.value)}
                                                disabled={loadingIdProofTypes}
                                                style={{ appearance: "none", WebkitAppearance: "none", paddingRight: "2rem" }}
                                            >
                                                {loadingIdProofTypes
                                                    ? <option value="">{t.loading}</option>
                                                    : <>
                                                        <option value="">{t.chooseValidDocument}</option>
                                                        {idProofTypes.map(type => (
                                                            <option key={type.id} value={type.id}>{t.idProofNames?.[type.name] || type.name}</option>
                                                        ))}
                                                      </>
                                                }
                                            </select>
                                            <i className="fa fa-chevron-down" style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "#6c757d", fontSize: "0.8rem" }}></i>
                                        </div>
                                        <div className="mt-3">
                                            <FileUpload label={t.uploadIdProofDoc} initialFile={dobProofDocFile} onChange={f => setDobProofDocFile(f)} />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">{t.anotherDocument}</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder={t.anotherDocumentPlaceholder}
                                            value={anotherDocName}
                                            onChange={e => setAnotherDocName(e.target.value)}
                                        />
                                        <div className="mt-3">
                                            <FileUpload label={t.uploadAnotherDoc} initialFile={anotherDocFile} onChange={f => setAnotherDocFile(f)} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Additional Documents */}

                            {/* Declaration */}
                            <div className="card info-status p-3 mt-3">
                                <div className="row align-items-center">
                                    <div className="col-auto">
                                        <div onClick={handleCheckboxClick} style={{ display: "inline-block", cursor: allFieldsFilled ? "pointer" : "not-allowed" }}>
                                            <input type="checkbox" className="form-check-input" checked={declared} onChange={() => {}} disabled={!allFieldsFilled} style={{ accentColor: "#28a745", pointerEvents: "none" }} />
                                        </div>
                                    </div>
                                    <div className="col">
                                        <span className="form-check-label">{t.diseaseClaimDeclText}</span>
                                    </div>
                                </div>
                            </div>
                            {/* Declaration */}
                            {declarationError && (
                                <div className="text-danger small mt-2">{declarationError}</div>
                            )}

                            {draftError && (
                                <div className="alert alert-danger mt-3">{draftError}</div>
                            )}
                            <div className="row mt-3">
                                <div className="col-md-12 text-end">
                                    <button className="btn btn-secondary me-3" onClick={() => navigate("/claimLoaneeIdentification")}><i className="fa fa-angle-left me-2"></i>{t.back}</button>
                                    <button className="btn btn-secondary me-3" disabled={draftSaving} onClick={handleSaveDraft}>
                                        {draftSaving
                                            ? <><span className="spinner-border spinner-border-sm me-2"></span>{t.saving}</>
                                            : <><i className="fa fa-save me-2"></i>{t.saveDraft}</>
                                        }
                                    </button>
                                    <button className="btn btn-primary me-3" onClick={handlePreview}><i className="fa fa-check-circle me-2"></i> {t.previewClaim}</button>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </section>
            {/* ==== Page Section Ends ==== */}

            <Footer />

            {draftSaved && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div className="card text-center p-5" style={{ maxWidth: "420px", width: "90%" }}>
                        <div className="mb-3">
                            <i className="fa fa-check-circle" style={{ fontSize: "3.5rem", color: "#25826a" }}></i>
                        </div>
                        <h4 className="mb-2">{t.draftSavedTitle}</h4>
                        <p className="text-muted mb-4">{t.deathClaimDraftSavedDesc}</p>
                        <button className="btn btn-primary" onClick={() => { setDraftSaved(false); navigate("/userDashboard"); }}>{t.ok}</button>
                    </div>
                </div>
            )}
        </>
    );
}

export default DeathClaim;
